import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { US_STATES, PROPERTY_TYPES } from "@/lib/listings";
import { Plus, MapPin, BedDouble, Bath, Search, Filter, X, Wifi, Sofa } from "lucide-react";

const propLabel = (val, lang) => {
  const m = PROPERTY_TYPES.find((p) => p.value === val);
  return m ? (lang === "ar" ? m.label_ar : m.label_en) : val;
};

const PropertyCard = ({ p, lang, t, onClick }) => (
  <button
    onClick={onClick}
    className="text-right group glass-card rounded-2xl overflow-hidden hover:border-yellow-500/60 transition-all"
    data-testid={`property-card-${p.property_id}`}
  >
    <div className="relative h-52 overflow-hidden">
      <ListingImage
        path={p.images?.[0]}
        alt={p.title}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent" />
      <Badge className="absolute top-3 right-3 bg-yellow-500 text-black hover:bg-yellow-400">
        {propLabel(p.property_type, lang)}
      </Badge>
      <div className="absolute bottom-3 right-3 text-yellow-400 font-bold text-lg">
        ${p.price?.toLocaleString()}<span className="text-sm text-slate-300">/{t("شهر", "mo")}</span>
      </div>
    </div>
    <div className="p-4 space-y-2">
      <h3 className="font-bold text-white truncate">{p.title}</h3>
      <div className="flex items-center gap-1 text-sm text-slate-400">
        <MapPin size={14} className="text-yellow-500" />
        <span className="truncate">{p.city}, {p.state} {p.zip_code}</span>
      </div>
      <div className="flex items-center gap-3 text-sm text-slate-300 pt-1">
        {p.bedrooms > 0 && (<span className="flex items-center gap-1"><BedDouble size={14} /> {p.bedrooms}</span>)}
        {p.bathrooms > 0 && (<span className="flex items-center gap-1"><Bath size={14} /> {p.bathrooms}</span>)}
        {p.furnished && (<span className="flex items-center gap-1 text-yellow-500"><Sofa size={14} /></span>)}
        {p.internet_included && (<span className="flex items-center gap-1 text-yellow-500"><Wifi size={14} /></span>)}
      </div>
    </div>
  </button>
);

export default function PropertyMarketplace() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    state: "", city: "", zip_code: "", property_type: "",
    min_price: "", max_price: "", bedrooms: ""
  });

  const load = async (f = filters) => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== null));
      const res = await axios.get(`${API}/properties`, { params });
      setProperties(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const apply = () => { load(filters); setShowFilters(false); };
  const reset = () => {
    const empty = { state: "", city: "", zip_code: "", property_type: "", min_price: "", max_price: "", bedrooms: "" };
    setFilters(empty); load(empty);
  };

  return (
    <DashboardLayout title={t("سوق السكن", "Housing Marketplace")}>
      <div className="space-y-6" data-testid="property-marketplace">
        {/* Hero */}
        <div className="relative glass-card rounded-3xl p-6 lg:p-10 overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-yellow-500/20 blur-[120px] rounded-full" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-amber-700/20 blur-[120px] rounded-full" />
          <div className="relative flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Badge className="bg-yellow-500/20 text-yellow-400 mb-3">{t("منصة السكن للعرب", "Housing Marketplace")}</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                {t("ابحث عن منزلك في أمريكا", "Find your home in America")}
              </h2>
              <p className="text-slate-400">{t("شقق، غرف، منازل، محلات، مكاتب — كل ما تحتاجه في مكان واحد", "Apartments, rooms, houses, shops, offices — all in one place")}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button onClick={() => navigate("/housing/mine")} variant="outline" className="border-slate-700 text-white" data-testid="my-properties-btn">
                {t("إعلاناتي", "My Listings")}
              </Button>
              <Button onClick={() => navigate("/housing/post")} className="btn-primary" data-testid="post-property-btn">
                <Plus className="ml-2" size={18} />
                {t("نشر عقار", "Post Property")}
              </Button>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div className="glass-card rounded-2xl p-4 flex flex-col md:flex-row gap-3">
          <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              value={filters.state}
              onChange={(e) => setFilters({ ...filters, state: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white"
              data-testid="filter-state"
            >
              <option value="">{t("كل الولايات", "All States")}</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>{language === "ar" ? s.name_ar : s.name_en}</option>
              ))}
            </select>
            <input
              placeholder={t("المدينة", "City")}
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white"
              data-testid="filter-city"
            />
            <input
              placeholder={t("الرمز البريدي", "ZIP code")}
              value={filters.zip_code}
              onChange={(e) => setFilters({ ...filters, zip_code: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white"
              data-testid="filter-zip"
            />
            <select
              value={filters.property_type}
              onChange={(e) => setFilters({ ...filters, property_type: e.target.value })}
              className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white"
              data-testid="filter-type"
            >
              <option value="">{t("كل الأنواع", "All Types")}</option>
              {PROPERTY_TYPES.map((p) => (
                <option key={p.value} value={p.value}>{language === "ar" ? p.label_ar : p.label_en}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowFilters(true)} variant="outline" className="border-slate-700 text-white" data-testid="more-filters-btn">
              <Filter size={16} className="ml-2" />
              {t("المزيد", "More")}
            </Button>
            <Button onClick={apply} className="btn-primary" data-testid="search-btn">
              <Search size={16} className="ml-2" />
              {t("بحث", "Search")}
            </Button>
          </div>
        </div>

        {/* Filters drawer (inline) */}
        {showFilters && (
          <div className="glass-card rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-white">{t("فلاتر متقدمة", "Advanced filters")}</h4>
              <button onClick={() => setShowFilters(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <input type="number" placeholder={t("أقل سعر", "Min price")} value={filters.min_price}
                onChange={(e) => setFilters({ ...filters, min_price: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white"
                data-testid="filter-min-price" />
              <input type="number" placeholder={t("أعلى سعر", "Max price")} value={filters.max_price}
                onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white"
                data-testid="filter-max-price" />
              <input type="number" placeholder={t("غرف النوم", "Bedrooms")} value={filters.bedrooms}
                onChange={(e) => setFilters({ ...filters, bedrooms: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-xl px-3 py-3 text-white"
                data-testid="filter-bedrooms" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button onClick={reset} variant="ghost" className="text-slate-300">{t("إعادة ضبط", "Reset")}</Button>
              <Button onClick={apply} className="btn-primary">{t("تطبيق", "Apply")}</Button>
            </div>
          </div>
        )}

        {/* Listings */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="glass-card rounded-2xl h-80 skeleton" />
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center">
            <p className="text-slate-400 mb-4">{t("لا توجد إعلانات تطابق البحث", "No listings match your search")}</p>
            <Link to="/housing/post"><Button className="btn-primary">{t("كن أول من ينشر", "Be the first to post")}</Button></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((p) => (
              <PropertyCard
                key={p.property_id}
                p={p}
                lang={language}
                t={t}
                onClick={() => navigate(`/housing/${p.property_id}`)}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
